import React from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Organization } from "./components/OrganizationSelector";
import { useAuth } from "./context/AuthContext";
import { useOrganization } from "./context/OrganizationContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import analytics from "./services/analytics";
import NotificationCenter from "./components/NotificationCenter";
import AIAssistantHolding from "./pages/AIAssistantHolding";
import AcceptInvitationPage from "./pages/auth/AcceptInvitationPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import Areas from "./pages/AreasTailwind";
import AreaDetail from "./pages/AreaDetailTailwind";
import Companies from "./pages/CompaniesTailwind";
import CompanyDetail from "./pages/CompanyDetailTailwind";
import Dashboard from "./pages/Dashboard";
import PositionDetailsTailwind from "./pages/PositionDetailsTailwind";
import PositioningTailwind from "./pages/PositioningTailwind";
import PropositionDetail from "./pages/PropositionDetailTailwind";
import Propositions from "./pages/PropositionsTailwind";
import Research from "./pages/Research";
import SegmentDetail from "./pages/SegmentDetailTailwind";
import NotificationsPage from "./pages/NotificationsPage";
import SegmentScoreMatrix from "./pages/SegmentScoreMatrix";
import Segments from "./pages/SegmentsTailwind";
import StrategicNarrative from "./pages/StrategicNarrative";
import StrategicNarrativeEditPage from "./pages/StrategicNarrativeEditPage";
// Removed: import Prioritization from "./pages/Prioritization";
// Removed: import GravityPlan from "./pages/GravityPlan";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import PlanningContextPanel from "./components/PlanningContextPanel";
import Sidebar from "./components/Sidebar";
import { GlobalUpgradeModal } from "./components/UpgradeModal";
import AccountManagement from "./pages/AccountManagement";
import GravityPlans from "./pages/GravityPlans";
import MyTasks from "./pages/MyTasks";
import ClaudeInstructions from "./pages/ClaudeInstructions";
import ObjectivesV2 from "./pages/ObjectivesV2";
import OKRDetail from "./pages/OKRDetail";
import OrganizationManagement from "./pages/OrganizationManagement";
import SuperAdminBilling from "./pages/SuperAdminBilling";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminManagement from "./pages/SuperAdminManagement";
import SuperAdminOrganizations from "./pages/SuperAdminOrganizations";
import SuperAdminUsers from "./pages/SuperAdminUsers";
import SuperAdminWebhooks from "./pages/SuperAdminWebhooks";
import {
  CurrentPlanningContext,
  planningCyclesApi,
} from "./services/planningCycles";

function Placeholder({ label }: { label: string }) {
  return (
    <h2 className="text-2xl font-bold text-gray-800">
      {label} (Coming soon...)
    </h2>
  );
}

// Helper function to get page name from path
function getPageNameFromPath(path: string): string {
  const routes: Record<string, string> = {
    "/": "Dashboard",
    "/dashboard": "Dashboard",
    "/segments": "Segments",
    "/propositions": "Propositions",
    "/positioning": "Positioning",
    "/companies": "Companies",
    "/research": "Research",
    "/objectives": "Objectives",
    "/gravity-plans": "Gravity Plans",
    "/my-tasks": "My Tasks",
    "/strategic-narrative": "Strategic Narrative",
    "/areas": "Areas",
    "/settings": "Settings",
    "/login": "Login",
    "/register": "Register",
  };

  // Check exact match first
  if (routes[path]) return routes[path];

  // Check for dynamic routes
  if (path.includes("/segments/")) return "Segment Detail";
  if (path.includes("/propositions/")) return "Proposition Detail";
  if (path.includes("/companies/")) return "Company Detail";
  if (path.includes("/objectives/")) return "OKR Detail";
  if (path.includes("/areas/")) return "Area Detail";

  return "Unknown Page";
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  if (user.role !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [planningPanelOpen, setPlanningPanelOpen] = React.useState(false);
  const [planningContext, setPlanningContext] =
    React.useState<CurrentPlanningContext | null>(null);
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const {
    organizations,
    selectedOrg,
    userRole,
    setSelectedOrg,
    refreshOrganizations,
  } = useOrganization();
  const location = useLocation();

  // Analytics is now initialized by PostHogProvider in index.tsx

  // Identify user when they log in or organization changes
  React.useEffect(() => {
    if (user && selectedOrg) {
      analytics.identify(user, selectedOrg, userRole || undefined);
    }
  }, [user, selectedOrg, userRole]);

  // Track page views
  React.useEffect(() => {
    const pageName = getPageNameFromPath(location.pathname);
    analytics.trackPageView(pageName);
  }, [location]);

  const handleOrganizationCreated = React.useCallback(
    async (_newOrg: Organization) => {
      // Refresh organizations list and select the new one
      // The 'true' parameter tells refreshOrganizations to select the newest org
      await refreshOrganizations(true);
    },
    [refreshOrganizations],
  );

  const handleLogout = () => {
    analytics.reset();
    logout();
  };

  // Fetch planning context when organization changes
  React.useEffect(() => {
    if (selectedOrg && token) {
      planningCyclesApi
        .getCurrentContext(selectedOrg.id)
        .then((context) => setPlanningContext(context))
        .catch((_err) => {
          // Error handling for planning context fetch
        });
    }
  }, [selectedOrg, token]);

  return (
    <NotificationProvider>
      <Toaster position="top-right" />
      <div className="flex h-screen bg-gray-100">
        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar - hidden on mobile by default, shown when menu is open */}
        {/* Note: The sidebar width is now dynamic (w-16 or w-64) based on collapsed state */}
        <div
          className={`${
            mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          } fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}
        >
          <Sidebar
            user={user}
            orgs={organizations}
            selectedOrg={selectedOrg}
            userOrgRole={userRole}
            onOrgChange={setSelectedOrg}
            onOrganizationCreated={handleOrganizationCreated}
            onLogout={handleLogout}
            onNavigate={() => setMobileMenuOpen(false)}
          />
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile header - only visible on small screens */}
          <header className="flex justify-between items-center p-4 bg-white border-b lg:hidden">
            <div className="flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
              <h1 className="ml-2 text-lg font-semibold text-gray-800">
                Spotlight
              </h1>
            </div>
            {/* Add notification bell to mobile header */}
            {user && <NotificationCenter />}
          </header>

          {/* Desktop header - only visible on large screens */}
          <header className="hidden lg:flex justify-between items-center px-6 py-3 bg-white border-b">
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedOrg ? selectedOrg.name : "Welcome"}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              {user && <NotificationCenter />}
            </div>
          </header>

          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-6">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/accept-invitation"
                element={<AcceptInvitationPage />}
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/research"
                element={
                  <ProtectedRoute>
                    <Research />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/research/assistant"
                element={
                  <ProtectedRoute>
                    <AIAssistantHolding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/propositions"
                element={
                  <ProtectedRoute>
                    <Propositions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/propositions/:id"
                element={
                  <ProtectedRoute>
                    <PropositionDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/positioning"
                element={
                  <ProtectedRoute>
                    <PositioningTailwind />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/positions/:positionId"
                element={
                  <ProtectedRoute>
                    <PositionDetailsTailwind />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/segments"
                element={
                  <ProtectedRoute>
                    <Segments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/segments/:segmentId"
                element={
                  <ProtectedRoute>
                    <SegmentDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies"
                element={
                  <ProtectedRoute>
                    <Companies />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:id"
                element={
                  <ProtectedRoute>
                    <CompanyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/areas"
                element={
                  <ProtectedRoute>
                    <Areas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/areas/:id"
                element={
                  <ProtectedRoute>
                    <AreaDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/matrix"
                element={
                  <ProtectedRoute>
                    <SegmentScoreMatrix />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gravity-plans"
                element={
                  <ProtectedRoute>
                    <GravityPlans />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-tasks"
                element={
                  <ProtectedRoute>
                    <MyTasks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy"
                element={
                  <ProtectedRoute>
                    <StrategicNarrative />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/new"
                element={
                  <ProtectedRoute>
                    <StrategicNarrativeEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategy/:id/edit"
                element={
                  <ProtectedRoute>
                    <StrategicNarrativeEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/okrs"
                element={
                  <ProtectedRoute>
                    <ObjectivesV2 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/okrs/:id"
                element={
                  <ProtectedRoute>
                    <OKRDetail />
                  </ProtectedRoute>
                }
              />
              {/* Removed: Old position-specific gravity plan route
            <Route
              path="/gravity-plans/:segmentId/:propositionId"
              element={
                <ProtectedRoute>
                  <GravityPlan />
                </ProtectedRoute>
              }
            /> */}
              {/* Keep old routes for backwards compatibility */}
              <Route
                path="/plan"
                element={<Navigate to="/gravity-plans" replace />}
              />
              <Route
                path="/prioritization"
                element={<Navigate to="/gravity-plans" replace />}
              />
              <Route
                path="/insights"
                element={
                  <ProtectedRoute>
                    <Placeholder label="Insights" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organization"
                element={
                  <ProtectedRoute>
                    <OrganizationManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              {/* Redirect old routes to new consolidated page */}
              <Route
                path="/team"
                element={<Navigate to="/organization" replace />}
              />
              <Route
                path="/organization/settings"
                element={<Navigate to="/organization" replace />}
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/claude-instructions"
                element={
                  <ProtectedRoute>
                    <ClaudeInstructions />
                  </ProtectedRoute>
                }
              />
              {/* Billing settings redirect */}
              <Route
                path="/settings/billing"
                element={<Navigate to="/organization?tab=billing" replace />}
              />
              {/* Super Admin Routes */}
              <Route
                path="/admin"
                element={
                  <SuperAdminRoute>
                    <SuperAdminDashboard />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/organizations"
                element={
                  <SuperAdminRoute>
                    <SuperAdminOrganizations />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <SuperAdminRoute>
                    <SuperAdminUsers />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/super-admins"
                element={
                  <SuperAdminRoute>
                    <SuperAdminManagement />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/system"
                element={
                  <SuperAdminRoute>
                    <div className="text-center py-8">
                      <h1 className="text-2xl font-bold text-gray-900">
                        System Settings
                      </h1>
                      <p className="text-gray-600 mt-2">Coming soon...</p>
                    </div>
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/billing"
                element={
                  <SuperAdminRoute>
                    <SuperAdminBilling />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/admin/webhooks"
                element={
                  <SuperAdminRoute>
                    <SuperAdminWebhooks />
                  </SuperAdminRoute>
                }
              />
            </Routes>
          </main>
        </div>

        {/* Planning Context Panel - show on specific pages (not Gravity Plans which has its own) */}
        {user &&
          selectedOrg &&
          planningContext &&
          (location.pathname === "/positioning" ||
            location.pathname === "/") && (
            <PlanningContextPanel
              isOpen={planningPanelOpen}
              onToggle={() => setPlanningPanelOpen(!planningPanelOpen)}
              currentCycle={
                planningContext.current_cycle
                  ? {
                      type: planningContext.current_cycle.cycle_type,
                      current: planningContext.current_cycle.name,
                      startDate: planningContext.current_cycle.start_date,
                      endDate: planningContext.current_cycle.end_date,
                      daysRemaining:
                        planningContext.current_cycle.days_remaining || 0,
                    }
                  : {
                      type: "quarter",
                      current: "No Active Cycle",
                      startDate: new Date().toISOString().split("T")[0],
                      endDate: new Date().toISOString().split("T")[0],
                      daysRemaining: 0,
                    }
              }
              okrs={
                planningContext.current_cycle?.okrs.map((okr) => ({
                  id: okr.id,
                  objective: okr.objective,
                  keyResults: okr.key_results.map((kr) => ({
                    id: kr.id,
                    description: kr.description,
                    target: kr.target_value,
                    current: kr.current_value,
                    status: kr.status,
                  })),
                })) || []
              }
              onReviewClick={() => navigate("/organization")}
              onPlanNextCycle={() => navigate("/organization")}
              onCycleChange={(cycleType) => {
                // Refresh planning context with new cycle type
                planningCyclesApi
                  .getCurrentContext(selectedOrg.id, cycleType)
                  .then((context) => setPlanningContext(context))
                  .catch((_err) => {
                    // Error handling for planning context fetch
                  });
              }}
            />
          )}

        {/* Global Upgrade Modal */}
        <GlobalUpgradeModal />
      </div>
    </NotificationProvider>
  );
}

export default App;
